import logger from '../../../utils/logger';
import { DeviceStatus } from '../../../constants/DeviceStatus';
import { beltSwitch } from '../device-switches/belt-switch';
import { startDecumDischargeSequence } from './start-decum-discharge-sequence';
import { timerKey } from '../../../utils/timer-manager/timer-manager';
import { checkDecumHoldConditions } from './check-decum-hold-conditions';
import type { IBatchStore } from '../batch-store-types';
import type { IWeighedDataToBatch } from '../../../../types/batch/DataToBatch';
import type { IBatchData } from '../../../containers/Batch/IBatchData';

/**
 * Validates discharge preconditions and schedules discharge when ready
 * This function is designed to be called recursively via setTimeout
 */
const validateAndScheduleDischargeWhenReady = (
  batchStore: IBatchStore,
  data: IWeighedDataToBatch,
  initialData: IBatchData,
  scaleRef: any = {},
) => {
  const { scale, material, mft, isFeedFinish } = data;
  const scaleWithMaterialId = `${scale.id}-${material.code}`;
  let scaleWithMaterialStatus = batchStore.getDeviceStatus(scaleWithMaterialId);
  let scaleStatus = batchStore.getDeviceStatus(scale.id);

  // Guard: No target weight configured
  if (!mft) {
    logger.log(`${material.code} has no target!`, data);
    return;
  }

  // Guard: Already completed discharge
  if (isFeedFinish) {
    logger.log(`DECUM ${scaleWithMaterialId} already finish!`);
    return;
  }

  // Hold state: Pause discharge and retry when hold is released
  if (checkDecumHoldConditions(batchStore, initialData, scale, scaleRef)) {
    batchStore.deviceStatus.set(scaleWithMaterialId, {
      ...scaleWithMaterialStatus,
      status: DeviceStatus.HOLD,
    });

    logger.log('hold decum', batchStore.isHoldFeed, initialData.isHoldLoad,
      batchStore.isHoldFeedDevice(scale.id), scale.parentScaleId,
      batchStore.isHoldDischarge, scaleRef.dischargeIsStop, scaleRef.dischargeIsResume);

    scheduleDischargeRetry(batchStore, data, initialData, scaleRef);
    return;
  }

  // Belt readiness: Start belt and wait for it to be running (except during washout)
  if (scale.beltId && !batchStore.isBeltOn(scale.beltId) && !initialData.isWashout) {
    beltSwitch(batchStore.rootStore, scale.beltId);
    logger.log(`scale ${scale.description || scale.id} wait for belt...`, scale.beltId);

    scheduleDischargeRetry(batchStore, data, initialData, scaleRef);
    return;
  }

  // Material availability check
  const scaleWeight = batchStore.getScaleWeight(scale);

  if (scaleStatus.totalFinalTarget < 0) {
    batchStore.showWarning(`Not enough material ${material.code} in ${scale.description} !`);
    logger.log(`Not enough material ${material.code} in ${scale.description} !`,
      scaleWeight, mft, scaleWithMaterialStatus.target, scaleStatus.totalFinalTarget);

    // Optionally hold until material is available
    if (scale.holdDischargeUntilThereIsEnoughMaterial) {
      batchStore.holdFeedDevice(scale.id);

      batchStore.deviceStatus.set(scaleWithMaterialId, {
        ...scaleWithMaterialStatus,
        status: DeviceStatus.HOLD,
      });

      scheduleDischargeRetry(batchStore, data, initialData, scaleRef);
      return;
    }
  }

  // All preconditions met - clear any pending retry timers
  batchStore.timers.clear(timerKey.device(scale.id));

  // Wait for downstream scale to be ready
  if (!batchStore.isScaleReadyForDischargeToNextScale(data, initialData)) {
    scheduleDischargeRetry(batchStore, data, initialData, scaleRef);
    return;
  }

  // Mark as ready to discharge (in delay before actual discharge)
  batchStore.deviceStatus.set(scaleWithMaterialId, {
    ...scaleWithMaterialStatus,
    status: DeviceStatus.DELAY,
  });

  // Calculate delay before discharge (for coordinating with other scales)
  const dischargeToNextScaleDelay = initialData.mixDesign && scale.dischargeAfterScaleId && scale.dischargeAfterScaleDelay
    ? scale.dischargeAfterScaleDelay * 1000
    : 1000;
  logger.log(`discharge ${scale.description} after scale delay #${scale.dischargeAfterScaleId}`, dischargeToNextScaleDelay);

  // Reset weight before attempting discharge
  batchStore.resetWeight(scale.inputPNo);

  // Schedule the actual discharge sequence
  batchStore.timers.clear(timerKey.dischargeToNextScale(scale.id));

  batchStore.timers.set(timerKey.dischargeToNextScale(scale.id), () => {
    startDecumDischargeSequence(batchStore, data, initialData, scaleRef);
  }, dischargeToNextScaleDelay, `Discharge to next scale #${scale.id}`);
};

/**
 * Schedules a retry of the discharge validation after 1 second
 */
const scheduleDischargeRetry = (
  batchStore: IBatchStore,
  data: any,
  initialData: any,
  scaleRef: any,
) => {
  const { scale } = data;
  batchStore.timers.set(timerKey.device(scale.id), () => {
    validateAndScheduleDischargeWhenReady(batchStore, data, initialData, scaleRef);
  }, 1000, `Discharge decum scale - scale #${scale.id}`);
};

/**
 * Initiates decum scale discharge process
 *
 * This is the main entry point for discharging a decum scale.
 * Use this function to start the discharge process - it will handle
 * all validation, waiting, and retry logic internally.
 */
export const dischargeDecumScale = (
  batchStore: IBatchStore,
  data: IWeighedDataToBatch,
  initialData: IBatchData,
  scaleRef: any = {},
) => {
  logger.log('discharge decum scale', data, initialData, scaleRef);

  // Begin the validation and scheduling process
  validateAndScheduleDischargeWhenReady(batchStore, data, initialData, scaleRef);
};

export const validateAndScheduleDecumScaleDischarge = validateAndScheduleDischargeWhenReady;
